import {
    Box,
    Grid,
} from '@mui/material';
import { Fragment, useEffect, useMemo } from 'react';
import { getIn } from 'formik';
import { CheckboxComponent } from '../inputComponents/CheckboxComponent';
import { InputComponent } from '../inputComponents/InputComponent';
import { SelectOption } from '../selectComponents/SelectOption';
import { GridItems } from './GridItems';
import '../../pages/AssignPromotion/AssignPromotionPage.css';
import './GridItems.css';
import {
    MixCodesProps,
    getMixCodeURI,
    MixedcodesEnvironment,
    MixedcodesCodeType,
} from '../../types/componentTypes/mixcodes-types';
import { usingVPC, getMixedcodesUrl } from '../../pages/EditPromotion/MixCodes/helpers/mixcodes-helper';
import MultiSelectAutocomplete from '../../common/MultiSelectAutocomplete/MultiSelectAutocomplete';
import MixCodesPrizeMappingComponent from '../../pages/EditPromotion/MixCodes/MixCodesPrizeMapping';
import { flowLabelMap } from '../../constants/lists';


const mapCheckboxState: { [key: string]: string[] } = {
    'tempParams.burnPincodes': [
        'secrets.mixCodesParameters',
        'tempParams.codeType',
        'tempParams.pincodeOriginValitidy',
    ],
    'tempParams.pincodeOriginValitidy': ['params.campaignIds', 'params.lotIds'],
    'tempParams.mixCodesPrizeMapping': [
        'params.mixCodesPrizeMapping.mapping'
    ],
};
const emptyValues = {
    'tempParams.burnPincodes': [
        { 'secrets.mixCodesParameters': [{ programId: '', secret: '', uri: '' }] },
        { 'tempParams.codeType': [] },
        { 'tempParams.pincodeOriginValitidy': false },
    ],
    'tempParams.pincodeOriginValitidy': [
        { 'params.campaignIds': [''] },
        { 'params.lotIds': [''] },
    ],
    'tempParams.mixCodesPrizeMapping': [
        { 'params.mixCodesPrizeMapping.mapping': [{ viralCode: '', prizes: [] }] },
    ],
};
const mapActions = {
    'tempParams.pincodeOriginValitidy': {
        param: 'checkerLambdas',
        value: 'pincodeOriginValidityCheckerLambda',
    },
    'tempParams.burnPincodes': { param: 'flowLambdas', value: 'burnPincodes' },
};

function useSyncMixCodes({ formik, firstCodeType, environment }: { formik: any; firstCodeType: MixedcodesCodeType, environment?: MixedcodesEnvironment; }) {
    useEffect(() => {
        if (!firstCodeType) return;

        const currentCodeTypes = formik.values.tempParams.codeType;
        const needToUpdateCodeTypes = !currentCodeTypes.every(
            (code: string) => code === firstCodeType
        );
        if (needToUpdateCodeTypes) {
            const updatedCodeTypes = currentCodeTypes.map(() => firstCodeType);
            formik.setFieldValue('tempParams.codeType', updatedCodeTypes);
        }

        const newUri = getMixedcodesUrl(firstCodeType, environment);

        const currentMixCodes = formik.values.secrets.mixCodesParameters;
        const needToUpdateMixCodes = !currentMixCodes.every(
            (record: any) => record.uri === newUri
        );
        if (needToUpdateMixCodes) {
            const updatedMixCodes = currentMixCodes.map((record: any) => ({
                ...record,
                uri: newUri,
            }));
            formik.setFieldValue('secrets.mixCodesParameters', updatedMixCodes);
        }
    }, [
        formik,
        firstCodeType,
        environment
    ]);
}

function MixCodesDetails({
    formik,
    isEdit = false,
    mainBoxClass,
    environment,
}: MixCodesProps) {
    const addValue = () => {
        const firstCodeType = formik.values.tempParams.codeType[0] || '';

        const computedUri = firstCodeType
            ? usingVPC()
            ? getMixCodeURI.uriVPCMap[firstCodeType]
            : getMixCodeURI.uriMap[firstCodeType]
            : '';
        const newMixCode = { programId: '', secret: '', uri: computedUri };

        formik.setFieldValue(
            'secrets.mixCodesParameters',
            [...formik.values.secrets.mixCodesParameters, newMixCode]
        );

        formik.setFieldValue(
            'tempParams.codeType',
        [...formik.values.tempParams.codeType, firstCodeType]
        );
    };

    const removeMixCodeValue = (params: { index: number }) => {
        formik.setFieldValue(
            'secrets.mixCodesParameters',
            formik.values.secrets.mixCodesParameters.filter(
                (_: any, i: number) => i !== params.index
            )
        );
        const codeValues = getIn(formik.values, 'tempParams.codeType');
        formik.setFieldValue(
            'tempParams.codeType',
            codeValues.filter((_: any, i: number) => i !== params.index)
        );
    };

    const handleMixcodesUri = (
        _: unknown,
        params: { index: number; option: any }
    ) => {
        if (!usingVPC() && !isEdit) {
            formik.setFieldValue('params.lotIds', []);
            formik.setFieldValue('params.campaignIds', []);
        }
        formik.setFieldValue(
            `secrets.mixCodesParameters[${params.index}]['uri']`,
            getMixedcodesUrl(params.option, environment)
        );

         if (params.option === 'Pincodes' || params.option === 'Viral Codes') {
            resetPrizeMapping();
        }
    };

    const removePincodeOriginValitidyFromCheckers = () => {
        const subAction = mapActions['tempParams.pincodeOriginValitidy'];
        formik.setFieldValue(
            subAction.param,
            formik.values[subAction.param].filter(
                (value: string) => value !== subAction.value
            )
        );
    };

    const removePincodeToCurrencyFromLambdas = () => {
        formik.values.flowLambdas  = formik.values.flowLambdas.filter((value:string) => value !== 'pincodeToCurrency');
    };

    const handleCheckbox = (e: any) => {
        const name = e.target.name;
        const checked = e.target.checked;
        const actions = mapActions[name as keyof typeof mapActions];

        if ((name === 'tempParams.pincodeOriginValitidy') &&
            !checked) {
            resetPrizeMapping();
        }

        const flow = formik.values.flow;

        if (!checked) {
            if (!isEdit) {
                mapCheckboxState[name].forEach((val) => {
                    const initialValue = getIn(formik.initialValues, val);
                    formik.setFieldValue(val, initialValue ?? false);
                    formik.setFieldTouched(val, false);
                });

                if(flow === "autoRedeemCnG"){
                    removePincodeToCurrencyFromLambdas();
                }
            } else {
                if (name === 'tempParams.burnPincodes') {
                    for (const keyValue in emptyValues) {
                        emptyValues[keyValue as keyof typeof emptyValues].forEach(
                            (val: any) => {
                            const [[key, value]] = Object.entries(val);
                            formik.setFieldValue(key, value !== "" ? value : false);
                        }
                    );
                    }
                    removePincodeOriginValitidyFromCheckers();
                } else {
                    emptyValues[name as keyof typeof emptyValues].forEach((val: any) => {
                        const [[key, value]] = Object.entries(val);
                        formik.setFieldValue(key, value !== "" ? value : false);
                    });
                    removePincodeOriginValitidyFromCheckers();
                }
            }
            formik.setFieldValue(
                actions.param,
                formik.values[actions.param].filter(
                    (value: string) => value !== actions.value
                )
            );
        } else {
            if(flow === "autoRedeemCnG" && name === 'tempParams.burnPincodes' ){
               formik.setFieldValue(actions.param, [
                    ...getIn(formik.values, actions.param),
                    actions.value, 'pincodeToCurrency'
                ])
            }else{
                formik.setFieldValue(actions.param, [
                ...getIn(formik.values, actions.param),
                actions.value,
            ])}
        }
    };

    const normalizeIds = (ids?: string[]): string[] => ids?.filter(Boolean) || [];
    const getFormikValue = (path: string) => getIn(formik.values, path);
    const codeTypeValues = getFormikValue('tempParams.codeType');
    const lotIdsError = getIn(formik.errors, 'params.lotIds');
    const campaignIdsError = getIn(formik.errors, 'params.campaignIds');
    const normalizedLotIds = normalizeIds(formik.values.params?.lotIds || []);
    const normalizedCampaignIds = normalizeIds(formik.values.params?.campaignIds || []);
    const hasLotIds = normalizedLotIds.length > 0;
    const hasCampaignIds = normalizedCampaignIds.length > 0;
    const showOriginValidityCheckbox = formik.values.tempParams.codeType.length
    const showLotField =formik.values.tempParams.pincodeOriginValitidy && (hasLotIds || codeTypeValues.includes('Pincodes'));
    const showCampaignField = formik.values.tempParams.pincodeOriginValitidy && (hasCampaignIds || codeTypeValues.includes('Viral Codes') );
    const firstCodeType: MixedcodesCodeType = useMemo(() => formik.values.tempParams.codeType?.[0], [formik.values.tempParams.codeType]);

    useSyncMixCodes({ formik, firstCodeType, environment });

    const disableMixCodesPrizeMapping = (!codeTypeValues) ||
    (!codeTypeValues.includes('Viral Codes') && !codeTypeValues.includes('Pincodes'));

    const resetPrizeMapping = () => {
        formik.setFieldValue('tempParams.mixCodesPrizeMapping', false);
        formik.setFieldValue('params.mixCodesPrizeMapping.mapping', []);
    };

    const iwFlowPresent = (formik): boolean => {
        if (!formik.values.flow) return false;

        const requiredFlows = [
            flowLabelMap["Instant Win"]
        ];

        for (const flowKey of requiredFlows) {
            if (formik.values.flow[flowKey] !== undefined) {
                return true;
            }
        }

        return false;
    };

    const isInstantWin = iwFlowPresent(formik);

    return (
        <Box className={mainBoxClass || 'text-box'}>
            <Box className='functionality-items mix-codes-details'>
                    <Grid container className='gridMixcodes'>
                        {formik.values?.secrets && formik.values.secrets.mixCodesParameters?.map((_: Object, index: number) => (
                            <Fragment key={index}>
                                <GridItems
                                    formik={formik}
                                    index={index}
                                    component={
                                        <Box className='mixcodes-box'>
                                            <InputComponent
                                                formik={formik}
                                                inputValue={getIn(formik.values, `secrets.mixCodesParameters[${index}]['programId']`)}
                                                formikLabel={`secrets.mixCodesParameters[${index}]['programId']`}
                                                title='Program ID'
                                                titleVariant='caption'
                                                label=''
                                                type='string'
                                            />
                                            <InputComponent
                                                formik={formik}
                                                inputValue={getIn(formik.values, `secrets.mixCodesParameters[${index}]['secret']`)}
                                                formikLabel={`secrets.mixCodesParameters[${index}]['secret']`}
                                                title='Secret Key'
                                                titleVariant='caption'
                                                label=''
                                                multiline={true}
                                            />
                                            <SelectOption
                                                formik={formik}
                                                formikValue={`tempParams.codeType[${index}]`}
                                                customAction={handleMixcodesUri}
                                                customParam={{ index }}
                                                disabled={index !== 0}
                                                optionsList={['Pincodes', 'Viral Codes']}
                                                title='Code Type'
                                                titleVariant='caption'
                                                errorPath={`secrets.mixCodesParameters`}
                                                dataTestId="code-type-select"
                                            />
                                        </Box>
                                    }
                                    addAction={addValue}
                                    removeAction={removeMixCodeValue}
                                    formikValueArray={'secrets.mixCodesParameters'}
                                />
                            </Fragment>
                        ))}
                    </Grid>
                    <CheckboxComponent
                    fieldName={'tempParams.pincodeOriginValitidy'}
                    label='Code origin validation'
                    disabledCondition={!showOriginValidityCheckbox}
                    customAction={handleCheckbox}
                />

                <Grid container className='gridPincodeOrigin'>
                {showLotField && (
                    <MultiSelectAutocomplete
                        label="Enter Allowed Lot ID"
                        ids={normalizedLotIds}
                        addNewItemLabel="Add New"
                        dialogTitle="Enter Allowed Lot ID"
                        onChange={(newIds) => {
                            formik.setFieldValue('params.lotIds', newIds);
                        }}
                        error={lotIdsError || ''}
                        dataTestId="add-lot"
                        labelVariant="caption"
                    />
                    )}

                    {showCampaignField && (
                    <MultiSelectAutocomplete
                        label="Enter Allowed Campaign ID"
                        ids={normalizedCampaignIds}
                        addNewItemLabel="Add New"
                        dialogTitle="Enter Allowed Campaign ID"
                        onChange={(newIds) => {
                            formik.setFieldValue('params.campaignIds', [...newIds]);
                        }}
                        error={campaignIdsError || ''}
                        dataTestId="add-campaign"
                        labelVariant="caption"
                    />
                    )}

                </Grid>

                {isInstantWin && (
                    <MixCodesPrizeMappingComponent
                    formik={formik}
                    disableMixCodesPrizeMapping={disableMixCodesPrizeMapping}
                />
                )}

            </Box>
        </Box>
    );
}

export { MixCodesDetails };
